/**
 * callManager.js — Merkezi arama yöneticisi
 * ---------------------------------------------------------------------------
 * NEDEN VAR (çözdüğü kök sorun):
 *   Önceki mimaride tüm arama mantığı `ChatCallPanel` içindeydi ve o bileşen
 *   YALNIZCA sohbet odası açıkken yükleniyordu. Sonuçları:
 *     • Kilit ekranından cevaplandığında uygulama ana sayfada açılıyor, panel
 *       yüklenmediği için aramaya KATILINMIYOR ve ses akmıyordu.
 *     • Reddetme sunucuya gitmiyordu → arayan 45 sn "aranıyor" görüyordu.
 *     • Biri X ile ayrıldığında karşı taraf haberdar olmuyordu.
 *
 *   Artık arama durumu UYGULAMA SEVİYESİNDE, modül içinde yaşıyor. Hangi sayfa
 *   açık olursa olsun aramalar doğru çalışır; arayüz sadece bu durumu okur.
 *
 * SUNUCU YÜKÜ: Polling yok. Tüm olaylar mevcut SSE bağlantısından ve native
 * köprüden itilir.
 * ---------------------------------------------------------------------------
 */

import {
  aramaBaslat as apiBaslat,
  aramayaKatil as apiKatil,
  aramayaHazir as apiHazir,
  aramayiReddet as apiReddet,
  aramadanAyril as apiAyril,
  aktifAramaAl,
  bekleyenGelenAramaAl,
  aramaOlaylarinaAbone,
  baglantiYapilandirmasiUret,
  mikrofonAkisiAl,
  webrtcDesteniyorMu,
  sseHazirBekle,
} from "./callService";
import webrtc from "./webrtcService";
import outgoingRingback from "./outgoingRingbackService";
import { shouldCleanupForIdentityChange } from "./callIdentityPolicy";
import {
  nativeAramaOlaylarinaAbone,
  nativeAramayiKapat,
  nativeAramaBasladi,
  nativeAramaBaglandi,
  nativeGidenAramaBaslat,
  nativeHoparlor,
  nativeSesliAramayaHazirla,
  nativeCevapIslendi,
  nativeGelenAramayiCevapla,
  nativeIOSAramayaKatil,
  nativeIOSSustur,
  nativeIOSMotoruMu,
  nativeBeklemeTonuBaslat,
  nativeBeklemeTonuDurdur,
  callKitAktifMi,
  callKitGosterdiMi,
} from "./nativeCallBridge";

/* ══════════════════════════════════════════════════════════════════════════
   DURUM
   ══════════════════════════════════════════════════════════════════════════ */

const BOS = {
  durum: "idle",      // idle | gelen | giden | baglaniyor | aktif
  callId: null,
  roomId: null,
  callFamilyId: null,
  arama: null,        // sunucudan gelen arama nesnesi
  gelen: null,        // { callId, roomId, fromUid, fromName, isGroup }
  susturuldu: false,
  hoparlor: false,    // varsayılan AHİZE (telefon görüşmesi davranışı)
  baslangic: 0,
  rtc: null,          // webrtcService canlı durumu
  kucuk: false,       // arama ekranı küçültülmüş mü
  hata: null,
};

let state = { ...BOS };
const aboneler = new Set();

let familyId = null;
let benimUid = null;
let kuruldu = false;

/* İŞLEM KİLİDİ — aynı anda birden fazla arama işlemi yürümesin.
   Başlatma/katılma sırasında sunucu bizi çoktan "bağlı" görürken yerel WebRTC
   oturumu henüz kurulmamış olur. Bu ara pencerede yapılan durum kontrolleri
   yanlış karar verip aramayı bozabiliyordu. */
let aktifIslem = null;
let baglantiZamanlayici = null;
let callActivitySon = null;
let bekleyenNativeCevap = null;
let bekleyenSonAramaOdaId = null;
let odayaGitFn = null;
let familyHazirlaFn = null;
let beklenenCallFamilyId = null;
let acilisUzlastirmaIslemi = null;
let sonAcilisUzlastirma = { anahtar: null, zaman: 0 };
let hoparlorDegisimNesli = 0;

const ACILIS_UZLASTIRMA_TEKRAR_MS = 1500;

function yayinla() {
  const kopya = { ...state };
  aboneler.forEach((fn) => {
    try { fn(kopya); } catch (_) { /* biri patlarsa diğerleri etkilenmesin */ }
  });
  const aktif = state.durum !== "idle";
  if (typeof window !== "undefined") {
    window.__AILE_CALL_ACTIVITY__ = {
      active: aktif,
      callId: state.callId,
      roomId: state.roomId,
    };
  }
  if (aktif !== callActivitySon && typeof window !== "undefined") {
    callActivitySon = aktif;
    window.dispatchEvent(new CustomEvent("fp:callActivity", {
      detail: { active: aktif, callId: state.callId, roomId: state.roomId },
    }));
  }
}

function ayarla(yeni) {
  state = { ...state, ...yeni };
  if (state.durum === "giden" && !state.hata) {
    if (nativeIOSMotoruMu()) {
      // iOS tonu CallKit'in playAndRecord oturumunda native çalar; bu sayede
      // sessiz anahtarından etkilenmez ve Web Audio ile çift ses oluşmaz.
      outgoingRingback.durdur();
    } else if (nativeBeklemeTonuBaslat()) {
      // Android native tonu STREAM_VOICE_CALL rotasını izler.
      outgoingRingback.durdur();
    } else {
      outgoingRingback.baslat();
    }
  } else {
    nativeBeklemeTonuDurdur();
    outgoingRingback.durdur();
  }
  yayinla();
}

export function aboneOl(fn) {
  if (typeof fn !== "function") return () => {};
  aboneler.add(fn);
  try { fn({ ...state }); } catch (_) { /* yoksay */ }
  return () => aboneler.delete(fn);
}

export function durumuAl() {
  return { ...state };
}

export function kimlikAta({ familyId: fid, uid }) {
  const yeniFamily = fid ? String(fid) : null;
  const yeniUid = uid ? String(uid) : null;
  const familyDegisti = familyId && yeniFamily && familyId !== yeniFamily;
  const kullaniciDegisti = benimUid && yeniUid && benimUid !== yeniUid;
  const beklenenCallGecisi = familyDegisti
    && beklenenCallFamilyId === yeniFamily
    && state.callFamilyId === yeniFamily
    && ["gelen", "baglaniyor"].includes(state.durum);
  const hesapDegisti = familyDegisti || kullaniciDegisti;
  const cleanupGerekli = shouldCleanupForIdentityChange({
    currentFamilyId: familyId,
    nextFamilyId: yeniFamily,
    currentUid: benimUid,
    nextUid: yeniUid,
    callState: state.durum,
    callFamilyId: state.callFamilyId,
    expectedCallFamilyId: beklenenCallFamilyId,
  });
  familyId = yeniFamily;
  benimUid = yeniUid;
  if (hesapDegisti) sonAcilisUzlastirma = { anahtar: null, zaman: 0 };
  if (cleanupGerekli) tumunuKapat();
  if (beklenenCallGecisi) beklenenCallFamilyId = null;
  if (bekleyenNativeCevap && familyId && benimUid) {
    const olay = bekleyenNativeCevap;
    bekleyenNativeCevap = null;
    setTimeout(() => nativeCevabiIsle(olay).catch(() => {}), 0);
  }
  if (bekleyenSonAramaOdaId && familyId && benimUid) {
    const odaId = bekleyenSonAramaOdaId;
    bekleyenSonAramaOdaId = null;
    setTimeout(() => sonAramalardanBaslat(odaId), 0);
  }
  if (familyId && benimUid) {
    setTimeout(() => bekleyenGelenAramayiUzlastir().catch(() => {}), 0);
  }
}

/**
 * Kill/arka plan sırasında kaçırılmış gelen çağrıyı uygulama kökündeki arama
 * ekranına taşır. Bu bir açılış/öne-geliş uzlaştırmasıdır; zamanlayıcılı ağ
 * sorgusu veya polling değildir.
 */
async function bekleyenGelenAramayiUzlastir({ zorla = false } = {}) {
  if (!familyId || !benimUid) return null;
  if (state.durum !== "idle" || aktifIslem) return null;

  const kontrolFamilyId = String(familyId);
  const kontrolUid = String(benimUid);
  const anahtar = `${kontrolFamilyId}:${kontrolUid}`;
  const simdi = Date.now();
  if (!zorla
      && sonAcilisUzlastirma.anahtar === anahtar
      && simdi - sonAcilisUzlastirma.zaman < ACILIS_UZLASTIRMA_TEKRAR_MS) {
    return acilisUzlastirmaIslemi;
  }
  if (acilisUzlastirmaIslemi) return acilisUzlastirmaIslemi;

  sonAcilisUzlastirma = { anahtar, zaman: simdi };
  acilisUzlastirmaIslemi = (async () => {
    try {
      const r = await bekleyenGelenAramaAl(kontrolFamilyId);

      // İstek sürerken hesap/aile, yerel arama durumu veya cevap akışı değiştiyse
      // eski sonucu uygulama. Native/SSE her zaman önceliklidir.
      if (String(familyId || "") !== kontrolFamilyId
          || String(benimUid || "") !== kontrolUid
          || state.durum !== "idle"
          || aktifIslem) return null;

      const call = r?.call;
      const incoming = r?.incoming || {};
      if (!call?.callId || !call?.roomId) return null;

      const ben = (call.participants || []).find(
        (p) => String(p?.uid || "") === kontrolUid
      );
      if (!ben || !["invited", "ringing"].includes(ben.state)) return null;
      if (String(call.startedByUid || "") === kontrolUid) return null;

      ayarla({
        durum: "gelen",
        callId: String(call.callId),
        roomId: String(call.roomId),
        callFamilyId: String(call.familyId || call.familyFirebaseId || kontrolFamilyId),
        arama: call,
        gelen: {
          callId: String(call.callId),
          roomId: String(call.roomId),
          fromUid: incoming.fromUid || call.startedByUid || "",
          fromName: incoming.fromName || "",
          fromAvatar: incoming.fromAvatar || "",
          roomName: incoming.roomName || "",
          isGroup: !!incoming.isGroup,
        },
        baslangic: call.answeredAt || call.startedAt || Date.now(),
        kucuk: false,
        hata: null,
      });
      return call;
    } catch (_) {
      // Açılış uzlaştırması yardımcı bir güvenlik ağıdır. Ağ hatasında mevcut
      // native CallKit/Android ve SSE akışları aynen çalışmaya devam eder.
      return null;
    } finally {
      acilisUzlastirmaIslemi = null;
    }
  })();

  return acilisUzlastirmaIslemi;
}

function sonAramalardanBaslat(roomId) {
  const odaId = roomId ? String(roomId) : "";
  if (!odaId) return;
  if (!familyId || !benimUid) {
    bekleyenSonAramaOdaId = odaId;
    return;
  }
  if (state.durum !== "idle") return;

  if (typeof odayaGitFn === "function") {
    try { odayaGitFn(odaId); } catch (_) { /* arama yine başlatılır */ }
  }
  baslat(odaId).catch((err) => {
    console.warn("[AileCall:recents] Arama başlatılamadı",
      err?.code || err?.message || "start_failed");
    ayarla({ hata: "startFailed" });
  });
}

/* ══════════════════════════════════════════════════════════════════════════
   TEMİZLİK
   ══════════════════════════════════════════════════════════════════════════ */

function tumunuKapat(nativeKapat = true) {
  const kapananCallId = state.callId;
  nativeBeklemeTonuDurdur();
  outgoingRingback.durdur();
  if (baglantiZamanlayici) {
    clearTimeout(baglantiZamanlayici);
    baglantiZamanlayici = null;
  }
  webrtc.oturumKapat();
  if (nativeKapat) {
    // Gecikmiş bir JS temizliği, hızlıca başlayan sonraki CallKit aramasını
    // kapatmasın. Native taraf yalnızca bu kimlik hâlâ aktifse işlemi uygular.
    nativeAramayiKapat(kapananCallId);
  }
  state = { ...BOS };
  yayinla();
}

function tekIslem(fn) {
  if (aktifIslem) return aktifIslem;
  aktifIslem = Promise.resolve().then(fn);
  return aktifIslem.finally(() => {
    aktifIslem = null;
  });
}

function baglantiZamanAsimiKur(callId, ms = 20000) {
  if (baglantiZamanlayici) clearTimeout(baglantiZamanlayici);
  baglantiZamanlayici = setTimeout(async () => {
    baglantiZamanlayici = null;
    if (String(state.callId || "") !== String(callId || "")) return;
    if (state.durum === "aktif") return;
    ayarla({ hata: "connectionTimeout" });
    await ayril();
  }, ms);
}

async function nativeCevabiIsle(olay) {
  const id = olay?.callId || state.gelen?.callId;
  const oda = olay?.roomId || state.gelen?.roomId;
  const targetFamilyId = String(
    olay?.familyId || olay?.familyFirebaseId || state.callFamilyId || state.gelen?.familyId || ""
  ).trim();

  /* Uygulama kill durumundan açılırken native cevap olayı React kimliği,
     aktif aile ve SSE kurulmadan önce gelebilir. Bu anda katılmaya çalışmak
     WebRTC oturumunu yanlış/eksik kimlikle başlatır; sonuç CallKit'te süre
     ilerler ama gerçek sohbete bağlanılmaz. Cevabı kısa süreli kuyrukta tutup
     kimlik hazır olur olmaz tek kez işliyoruz. */
  if (!id) return;

  if (targetFamilyId && targetFamilyId !== familyId) {
    if (typeof familyHazirlaFn !== "function") {
      bekleyenNativeCevap = { ...olay, callId: id, roomId: oda || null, ts: Date.now() };
      return;
    }
    beklenenCallFamilyId = targetFamilyId;
    ayarla({
      callId: String(id),
      roomId: oda ? String(oda) : state.roomId,
      callFamilyId: targetFamilyId,
      hata: null,
    });
    try {
      const snapshot = await familyHazirlaFn(targetFamilyId);
      kimlikAta({ familyId: snapshot.familyId, uid: benimUid });
    } catch (error) {
      bekleyenNativeCevap = { ...olay, callId: id, roomId: oda || null, ts: Date.now() };
      ayarla({
        durum: "gelen",
        callId: String(id),
        roomId: oda ? String(oda) : state.roomId,
        callFamilyId: targetFamilyId,
        gelen: {
          ...(state.gelen || {}),
          callId: String(id),
          roomId: oda ? String(oda) : state.roomId,
          familyId: targetFamilyId,
        },
        hata: "familySwitchFailed",
      });
      return;
    }
  }

  /* CallKit cevabı geldiğinde iOS native motoru CXAnswerCallAction içinde
     zaten start() edilmiştir. WebView uyandığında aynı çağrıya ikinci kez
     "katıl" komutu göndermek ikinci bir cevap/temizleme yarışı oluşturuyordu:

       native join -> WebView yeniden katıl/temizle -> native leave -> ready 404

     iOS'ta JS yalnızca arayüz durumunu eşler. Kalıcı cevap kuyruğu ancak
     nativeReady olayı geldikten sonra temizlenir. */
  if (nativeIOSMotoruMu()) {
    ayarla({
      durum: state.durum === "aktif" ? "aktif" : "baglaniyor",
      callId: String(id),
      roomId: oda ? String(oda) : state.roomId,
      callFamilyId: targetFamilyId || familyId,
      gelen: null,
      hata: null,
    });
    if (oda && typeof odayaGitFn === "function") {
      try { odayaGitFn(String(oda)); } catch (_) { /* arama yine devam eder */ }
    }
    return;
  }

  if (!familyId || !benimUid) {
    bekleyenNativeCevap = {
      ...olay,
      callId: id,
      roomId: oda || olay?.roomId || null,
      ts: Date.now(),
    };
    setTimeout(() => {
      if (bekleyenNativeCevap && Date.now() - bekleyenNativeCevap.ts >= 60000) {
        bekleyenNativeCevap = null;
      }
    }, 61000);
    return;
  }

  try {
    await katil(id);
    const joinedRoomId = state.roomId || oda;
    if (joinedRoomId && typeof odayaGitFn === "function") {
      try { odayaGitFn(String(joinedRoomId)); } catch (_) { /* arama yine devam eder */ }
    }
    nativeCevapIslendi(id);
    if (!nativeIOSMotoruMu()) webrtc.sesCikisiniCanlandir();
  } catch (error) {
    console.warn("[AileCall:manager] Native cevap katılıma dönüşmedi",
      error?.code || error?.message || "join_failed");
    ayarla({ hata: "joinFailed" });
    nativeCevapIslendi(id);
    tumunuKapat();
  }
}

/* ══════════════════════════════════════════════════════════════════════════
   WEBRTC OTURUMU
   ══════════════════════════════════════════════════════════════════════════ */

async function oturumKur(call, iceServers) {
  /* ÖNCEKİ OTURUMU KESİN KAPAT ve cihazın mikrofonu/ICE portlarını serbest
     bırakması için bir kare bekle. Aksi hâlde aramayı kapatıp HEMEN yeniden
     aradığında yeni bağlantı kurulamıyor, karşı taraf "bağlanıyor"da kalıyordu.
     Bir süre bekleyince çalışmasının sebebi buydu — kaynaklar zaman aşımıyla
     serbest kalıyordu. */
  webrtc.oturumKapat();
  await nativeSesliAramayaHazirla();
  const akis = await mikrofonAkisiAl();
  const rtcConfig = await baglantiYapilandirmasiUret(iceServers);

  webrtc.oturumBaslat({
    callId: call.callId,
    roomId: call.roomId,
    benimUid,
    yerelAkis: akis,
    rtcConfig,
  });

  nativeAramaBasladi();                    // Android: mikrofon ön plan servisi
  // Telefon görüşmesi standardı: her yeni arama ahizeden başlar.
  nativeHoparlor(false);
  webrtc.sesCikisiniCanlandir();
}

/* ══════════════════════════════════════════════════════════════════════════
   EYLEMLER
   ══════════════════════════════════════════════════════════════════════════ */

/** Arama başlatır (premium gerekir). */
export async function baslat(roomId, caller = {}) {
  // Kullanıcı hareketi sürerken iOS/Safari ses çıkışını hazırla. Ton ancak
  // sunucu aramayı oluşturup durum "giden" olduğunda başlar.
  if (!nativeIOSMotoruMu()) outgoingRingback.hazirla();
  if (!webrtcDesteniyorMu()) {
    const e = new Error("WEBRTC_UNSUPPORTED"); e.code = "WEBRTC_UNSUPPORTED"; throw e;
  }
  if (state.durum !== "idle") return state;
  return tekIslem(() => baslatIc(roomId, caller));
}

async function baslatIc(roomId, caller = {}) {

  let r;
  try {
    r = await apiBaslat(familyId, roomId, caller);
  } catch (err) {
    /* Odada zaten canlı bir arama varsa (çoğu zaman düzgün kapanmamış eski
       bir arama) hata vermek yerine ONA KATIL. Kullanıcı "bağlanıyor"da
       takılı kalmak yerine konuşmaya başlar. */
    const kod = err?.code || err?.data?.code;
    if (kod === "CALL_ALREADY_ACTIVE") {
      const mevcut = err?.data?.call || err?.payload?.call;
      if (mevcut?.callId) { await katilIc(mevcut.callId); return state; }
      const a = await aktifAramaAl(familyId, roomId).catch(() => null);
      if (a?.call?.callId) { await katilIc(a.call.callId); return state; }
    }
    throw err;
  }
  if (!r?.call) throw new Error("start_failed");

  ayarla({
    durum: "giden",
    callId: r.call.callId,
    roomId: String(r.call.roomId),
    callFamilyId: String(r.call.familyId || r.call.familyFirebaseId || familyId),
    arama: r.call,
    hoparlor: false,
    baslangic: 0,
    hata: null,
  });
  try {
    await nativeGidenAramaBaslat({
      callId: r.call.callId,
      roomId: r.call.roomId,
      displayName: r.call.roomName || "Aile Yönetimi",
    });
    if (nativeIOSMotoruMu()) {
      // iOS'ta join/ready/SSE/SDP/ICE ve ses tamamen yerel motordadır.
      // WKWebView arka planda askıya alınsa bile arama devam eder.
      return state;
    }
    const sseSozu = sseHazirBekle(familyId);
    await oturumKur(r.call, r.iceServers);
    if (!(await sseSozu)) {
      const e = new Error("SSE_NOT_READY"); e.code = "SSE_NOT_READY"; throw e;
    }
    const hazir = await apiHazir(r.call.callId);
    if (hazir?.call) {
      ayarla({ arama: hazir.call });
      await webrtc.hazirEslerleBaglan(hazir.call.participants);
      webrtc.sesCikisiniCanlandir();
    }
  } catch (e) {
    console.warn("[AileCall:manager] Arama başlatma hazırlığı başarısız",
      e?.code || e?.message || "start_failed");
    await apiAyril(r.call.callId).catch(() => {});
    tumunuKapat();
    throw e;
  }
  return state;
}

/** Aramaya katılır (premium gerekmez). */
export async function katil(callId) {
  if (!callId) return;
  if (state.durum === "aktif" && String(state.callId) === String(callId)) return;
  if (state.durum === "baglaniyor"
      && String(state.callId) === String(callId)
      && aktifIslem) return aktifIslem;
  return tekIslem(() => katilIc(callId));
}

/**
 * Gelen arama ekranındaki yeşil düğmenin tek giriş noktası.
 *
 * iOS'ta ses oturumunu yalnızca CallKit etkinleştirebilir. Bu nedenle ekran
 * kilidi push'u gelmişse mevcut CXCall cevaplanır; APNs gecikmiş ama SSE
 * gelmişse aynı çağrı önce yerel CallKit'e bildirilip hemen cevaplanır.
 * Android/web doğrudan mevcut WebRTC katılım akışını kullanır.
 */
export async function cevapla(callId = null) {
  const id = callId || state.gelen?.callId || state.callId;
  if (!id) return state;

  if (nativeIOSMotoruMu()) {
    const gelen = state.gelen || {};
    const roomId = gelen.roomId || state.roomId || "";
    const basladi = nativeGelenAramayiCevapla({
      callId: id,
      roomId,
      familyId: state.callFamilyId || familyId || "",
      fromUid: gelen.fromUid || "",
      fromName: gelen.fromName || "Aile Yönetimi",
      fromAvatar: gelen.fromAvatar || "",
      isGroup: !!gelen.isGroup,
    });
    if (basladi) {
      ayarla({
        durum: "baglaniyor",
        callId: String(id),
        roomId: roomId ? String(roomId) : state.roomId,
        gelen: null,
        hata: null,
      });
      return state;
    }
  }

  return katil(id);
}

async function katilIc(callId) {
  ayarla({
    durum: "baglaniyor",
    callId: String(callId),
    gelen: null,
    hoparlor: false,
    baslangic: 0,
    hata: null,
  });
  if (nativeIOSMotoruMu()) {
    nativeIOSAramayaKatil(callId, state.roomId || "", state.callFamilyId || familyId || "");
    return state;
  }
  try {
    const r = await apiKatil(callId);
    if (!r?.call) throw new Error("join_failed");
    const responseFamilyId = String(r.call.familyId || r.call.familyFirebaseId || "").trim();
    if (responseFamilyId && responseFamilyId !== familyId) {
      if (typeof familyHazirlaFn !== "function") throw new Error("CALL_FAMILY_NOT_READY");
      beklenenCallFamilyId = responseFamilyId;
      ayarla({ callFamilyId: responseFamilyId });
      const snapshot = await familyHazirlaFn(responseFamilyId);
      kimlikAta({ familyId: snapshot.familyId, uid: benimUid });
    }
    ayarla({
      callId: r.call.callId,
      roomId: String(r.call.roomId),
      callFamilyId: responseFamilyId || state.callFamilyId || familyId,
      arama: r.call,
    });

    const sseSozu = sseHazirBekle(familyId);
    await oturumKur(r.call, r.iceServers);
    if (!(await sseSozu)) {
      const e = new Error("SSE_NOT_READY"); e.code = "SSE_NOT_READY"; throw e;
    }
    const hazir = await apiHazir(r.call.callId);
    if (hazir?.call) {
      ayarla({ arama: hazir.call });
      await webrtc.hazirEslerleBaglan(hazir.call.participants);
      webrtc.sesCikisiniCanlandir();
    }
    baglantiZamanAsimiKur(r.call.callId);
    return state;
  } catch (e) {
    console.warn("[AileCall:manager] Aramaya katılım başarısız",
      e?.code || e?.message || "join_failed");
    await apiAyril(callId).catch(() => {});
    tumunuKapat();
    throw e;
  }
}

/**
 * Aramayı reddeder.
 * ÖNEMLİ: Sunucuya HER ZAMAN bildirilir — böylece arayan tarafta 45 saniye
 * "aranıyor" yazmak yerine anında "reddedildi" görünür.
 */
export async function reddet(callId = null) {
  const id = callId || state.gelen?.callId || state.callId;
  tumunuKapat();
  if (id) await apiReddet(id).catch(() => {});
}

/** Aramadan ayrılır / bitirir. */
export async function ayril() {
  const id = state.callId;
  tumunuKapat();
  if (nativeIOSMotoruMu()) return;
  if (id) await apiAyril(id).catch(() => {});
}

export function susturmaDegistir() {
  if (nativeIOSMotoruMu()) {
    const yeni = !state.susturuldu;
    nativeIOSSustur(yeni);
    ayarla({ susturuldu: yeni });
    return yeni;
  }
  const yeni = webrtc.susturmaDegistir();
  ayarla({ susturuldu: yeni });
  return yeni;
}

/** Arama ekranını büyüt (sohbetteki şeritten çağrılır). */
export function ekraniAc() {
  if (state.durum === "idle") return;
  ayarla({ kucuk: false });
}

/** Arama ekranını küçült — kullanıcı sohbete dönebilir. */
export function ekraniKucult() {
  ayarla({ kucuk: true });
}

export function hoparlorDegistir() {
  const yeni = !state.hoparlor;
  const buDegisim = ++hoparlorDegisimNesli;
  ayarla({ hoparlor: yeni });

  // Hoparlör düğmesi doğrudan kullanıcı hareketidir; iOS Web Audio çıkışını
  // bu hareket içinde açık tut. Native yönlendirme tamamlanınca bekleme tonu
  // yeni ses yolunda yeniden oluşturulur.
  if (state.durum === "giden" && !nativeIOSMotoruMu()) outgoingRingback.hazirla();
  const nativeSonuc = nativeHoparlor(yeni);
  let tazelendi = false;
  const cikisiCanlandir = () => {
    if (tazelendi || buDegisim !== hoparlorDegisimNesli) return;
    tazelendi = true;
    if (state.durum === "giden" && !nativeIOSMotoruMu()) {
      outgoingRingback.cikisiTazele();
    }
    webrtc.sesCikisiniCanlandir();
  };

  // Android eklentisi ses yolu gerçekten değişince Promise'i tamamlar. iOS
  // message handler senkron bildirimdir; kısa run-loop payından sonra yenile.
  Promise.resolve(nativeSonuc)
    .catch(() => false)
    .finally(() => setTimeout(cikisiCanlandir, 80));
  setTimeout(cikisiCanlandir, 450); // native geri dönüş gelmezse güvenlik ağı
  return yeni;
}

/** Oda açılırken canlı arama var mı? (TEK SEFER — polling değil) */
export async function odayiKontrolEt(roomId) {
  if (!familyId || !roomId) return null;
  if (state.durum !== "idle") return null;
  // Katılma/başlatma sürerken durum yanıltıcıdır — karışma.
  if (aktifIslem) return null;
  try {
    const r = await aktifAramaAl(familyId, roomId);
    if (!r?.call) return null;
    const ben = (r.call.participants || []).find((p) => String(p.uid) === String(benimUid));

    /* ⚠️ BURADA ASLA OTOMATİK "leave" GÖNDERİLMEZ.
       Önceki sürümde, sunucu beni "bağlı" görüp yerel oturum henüz kurulmamışsa
       bunu "hayalet katılım" sayıp `leave` yolluyordu. Ama cevaplama akışında
       tam olarak bu durum NORMALDİR: sunucuya katılma isteği gider, ardından
       mikrofon izni beklenirken oda açılır ve bu kontrol çalışır. Sonuç: yeni
       katıldığın arama anında kapanıyor, "bağlanıyor"da kalıyordu.

       Doğru davranış: oturumum yoksa sadece AFİŞİ göster — kullanıcı isterse
       "Katıl" ile yeniden bağlanır. Sunucudaki asılı aramaları zaten
       zaman aşımı süpürücüsü temizliyor. */
    if (ben && ben.state === "joined" && webrtc.oturumVarMi()) return null;
    ayarla({
      durum: "gelen",
      callId: r.call.callId,
      roomId: String(r.call.roomId),
      arama: r.call,
      gelen: {
        callId: r.call.callId,
        roomId: String(r.call.roomId),
        fromUid: r.call.startedByUid,
        devamEden: true,
      },
      baslangic: r.call.answeredAt || r.call.startedAt || Date.now(),
    });
    return r.call;
  } catch (_) {
    return null;
  }
}

/* ══════════════════════════════════════════════════════════════════════════
   OLAY DİNLEYİCİLERİ — uygulama açılışında BİR KEZ kurulur
   ══════════════════════════════════════════════════════════════════════════ */

export function kur({ odayaGit, familyHazirla } = {}) {
  if (kuruldu) return;
  kuruldu = true;
  odayaGitFn = typeof odayaGit === "function" ? odayaGit : null;
  familyHazirlaFn = typeof familyHazirla === "function" ? familyHazirla : null;

  // webrtcService canlı durumu (kim konuşuyor, bağlantı durumu)
  webrtc.durumDinleyiciAta((d) => {
    if (!d) {
      ayarla({ rtc: null });
      return;
    }
    /* ICE "connected" yalnızca ağ yolunun açık olduğunu söyler. Aramayı ancak
       karşı taraftan gerçek RTP ses paketi geldikten sonra aktif say. */
    const ilkGercekBaglanti = Number(d.medyaHazirSayisi || 0) > 0
      && state.durum !== "aktif";
    if (ilkGercekBaglanti) {
      if (baglantiZamanlayici) {
        clearTimeout(baglantiZamanlayici);
        baglantiZamanlayici = null;
      }
      nativeAramaBaglandi();
      nativeHoparlor(state.hoparlor);
      webrtc.sesCikisiniCanlandir();
      const baglananCallId = state.callId;
      setTimeout(() => {
        if (!baglananCallId || String(state.callId || "") !== String(baglananCallId)) return;
        nativeHoparlor(state.hoparlor);
        webrtc.sesCikisiniCanlandir();
      }, 900);
      ayarla({
        rtc: d,
        durum: "aktif",
        baslangic: Date.now(),
        hata: null,
      });
      return;
    }
    ayarla({ rtc: d });
  });

  /* ── Sunucu olayları (SSE) ── */
  aramaOlaylarinaAbone(async (olay) => {
    if (!olay?.type) return;

    if (olay.type === "call:incoming") {
      /* iOS'ta gelen arama normalde CallKit'ten gelir. AMA VoIP push herhangi
         bir sebeple ulaşmazsa (token tazelenmemiş, ağ, APNs gecikmesi) eskiden
         HİÇBİR ŞEY olmuyordu — Android'den iPhone'u arayınca hiç tepki
         vermemesinin sebebi buydu.

         Artık YEDEK YOL var: CallKit bu aramayı gerçekten gösterdiyse burada
         bir şey yapmayız (çift arayüz olmasın); göstermediyse uygulama içi
         arayüzü açarız. Kısa bir gecikme, push'un CallKit'e ulaşmasına şans
         tanır; gelirse yedek iptal edilir. */
      if (state.durum !== "idle") return;
      if (callKitAktifMi()) {
        const id = olay.callId;
        setTimeout(() => {
          if (callKitGosterdiMi(id)) return;      // CallKit hallettti
          if (state.durum !== "idle") return;     // Bu arada başka şey oldu
          if (aktifIslem) return;                  // Katılma sürüyor — karışma
          ayarla({
            durum: "gelen",
            callId: id,
            roomId: String(olay.roomId),
            callFamilyId: String(olay.familyId || olay.familyFirebaseId || familyId),
            gelen: {
              callId: id,
              roomId: String(olay.roomId),
              familyId: String(olay.familyId || olay.familyFirebaseId || familyId),
              fromUid: olay.fromUid,
              fromName: olay.fromName || "",
              isGroup: !!olay.isGroup,
            },
          });
        }, 5000);
        return;
      }
      ayarla({
        durum: "gelen",
        callId: olay.callId,
        roomId: String(olay.roomId),
        callFamilyId: String(olay.familyId || olay.familyFirebaseId || familyId),
        gelen: {
          callId: olay.callId,
          roomId: String(olay.roomId),
          familyId: String(olay.familyId || olay.familyFirebaseId || familyId),
          fromUid: olay.fromUid,
          fromName: olay.fromName || "",
          isGroup: !!olay.isGroup,
        },
      });
      return;
    }

    // Bundan sonrası yalnızca kendi aramamız
    if (olay.callId && state.callId && String(olay.callId) !== String(state.callId)) return;

    if (olay.type === "call:signal") {
      if (nativeIOSMotoruMu()) return; // iOS sinyali yerel SSE motoru işler
      await webrtc.sinyalIsle(olay);
      return;
    }

    if (olay.type === "call:participant-joined") {
      if (nativeIOSMotoruMu()) {
        ayarla({
          arama: state.arama ? {
            ...state.arama,
            joinedCount: olay.joinedCount ?? state.arama.joinedCount,
            participants: guncelleKatilimci(state.arama.participants, olay.uid, "joined"),
          } : state.arama,
        });
        return;
      }
      if (webrtc.oturumVarMi()) {
        await webrtc.tekliGonder(olay.uid);
        if (state.durum === "giden" || state.durum === "baglaniyor") {
          baglantiZamanAsimiKur(state.callId);
        }
      }
      ayarla({
        arama: state.arama ? {
          ...state.arama,
          joinedCount: olay.joinedCount ?? state.arama.joinedCount,
          participants: guncelleKatilimci(state.arama.participants, olay.uid, "joined"),
        } : state.arama,
      });
      return;
    }

    if (olay.type === "call:participant-left") {
      webrtc.esKapat(olay.uid);
      ayarla({
        arama: state.arama ? {
          ...state.arama,
          joinedCount: olay.joinedCount ?? state.arama.joinedCount,
          participants: guncelleKatilimci(state.arama.participants, olay.uid, "left"),
        } : state.arama,
      });
      return;
    }

    if (olay.type === "call:declined") {
      ayarla({ hata: "declined" });
      return;
    }

    if (olay.type === "call:ended") {
      /* iOS native motoru aynı sunucu olayını kendi SSE bağlantısından alıp
         CallKit'i kapatır. WebView'in olayı tekrar native `leave` olarak geri
         göndermesi join/ready akışını yarışta öldürüyordu. */
      tumunuKapat(!nativeIOSMotoruMu());
    }
  });

  /* ── Native olaylar (CallKit / Android bildirimi) ── */
  nativeAramaOlaylarinaAbone(async (olay) => {
    if (!olay?.type) return;

    if (olay.type === "recentsStartCall") {
      sonAramalardanBaslat(olay.roomId);
      return;
    }

    if (olay.type === "nativeSpeaker") {
      ayarla({ hoparlor: !!olay.speaker });
      return;
    }

    if (olay.type === "nativeConnecting") {
      ayarla({
        durum: state.durum === "giden" ? "giden" : "baglaniyor",
        callId: olay.callId || state.callId,
        roomId: olay.roomId || state.roomId,
        hata: null,
      });
      return;
    }

    if (olay.type === "nativeReady") {
      ayarla({
        durum: state.durum === "giden" ? "giden" : "baglaniyor",
        callId: olay.callId || state.callId,
        roomId: olay.roomId || state.roomId,
        arama: {
          ...(state.arama || {}),
          callId: olay.callId || state.callId,
          roomId: olay.roomId || state.roomId,
          participants: olay.participants || state.arama?.participants || [],
        },
        hata: null,
      });
      nativeCevapIslendi(olay.callId || state.callId);
      return;
    }

    if (olay.type === "nativeConnected") {
      if (baglantiZamanlayici) {
        clearTimeout(baglantiZamanlayici);
        baglantiZamanlayici = null;
      }
      ayarla({
        durum: "aktif",
        callId: olay.callId || state.callId,
        roomId: olay.roomId || state.roomId,
        baslangic: Date.now(),
        hata: null,
      });
      return;
    }

    if (olay.type === "nativeFailed") {
      console.warn("[AileCall:native]", olay.message || "native_failed");
      ayarla({ hata: "joinFailed" });
      tumunuKapat();
      return;
    }

    if (olay.type === "answerFailed") {
      ayarla({
        durum: "gelen",
        callId: olay.callId || state.callId,
        gelen: state.gelen || {
          callId: olay.callId || state.callId,
          roomId: olay.roomId || state.roomId,
        },
        hata: "joinFailed",
      });
      return;
    }

    if (olay.type === "nativeEnded") {
      tumunuKapat(false);
      return;
    }

    if (olay.type === "answer") {
      await nativeCevabiIsle(olay);
      return;
    }

    if (olay.type === "end") {
      /* CallKit / bildirim üzerinden REDDEDİLDİ veya BİTİRİLDİ.
         Aramaya katılmışsak "ayrıl", katılmamışsak "reddet" gönderilir —
         böylece arayan taraf anında doğru bilgiyi görür. */
      if (nativeIOSMotoruMu()) {
        // CallKit eylemi sunucu leave/decline isteğini yerel motorda yaptı.
        tumunuKapat(false);
      } else if (state.durum === "aktif" || state.durum === "giden") {
        await ayril();
      } else {
        await reddet(olay.callId);
      }
      return;
    }

    if (olay.type === "mute") {
      if (webrtc.susturulduMu() !== !!olay.muted) susturmaDegistir();
      return;
    }

    if (olay.type === "cancel") {
      if (olay.callId && state.callId
          && String(olay.callId) !== String(state.callId)) return;
      tumunuKapat();
      return;
    }

    if (olay.type === "incoming" && state.durum === "idle") {
      const incomingFamilyId = olay.familyId || olay.familyFirebaseId || familyId || null;
      ayarla({
        durum: "gelen",
        callId: olay.callId,
        roomId: olay.roomId ? String(olay.roomId) : null,
        callFamilyId: incomingFamilyId ? String(incomingFamilyId) : null,
        gelen: {
          callId: olay.callId,
          roomId: olay.roomId ? String(olay.roomId) : null,
          familyId: incomingFamilyId ? String(incomingFamilyId) : null,
          fromUid: olay.fromUid || "",
          fromName: olay.fromName || "",
          fromAvatar: olay.fromAvatar || "",
          isGroup: !!olay.isGroup,
        },
      });
    }
  });

  /* pagehide üzerinde leave gönderilmez. iOS/Android WebView, ekran
     kilitlenirken veya uygulama arka plana alınırken pagehide üretebilir;
     bunu ayrılma saymak çalışan görüşmeyi yanlışlıkla bitiriyordu. Ayrılma
     yalnızca kullanıcı eylemi/native sonlandırma ya da sunucu zaman aşımıdır. */

  /* Uygulama arka plandan görünür hâle geldiğinde bir kez uzlaştır. focus ve
     pageshow bazı WebView sürümlerinde visibilitychange olmadan çalıştığı için
     üç olay da dinlenir; kısa tekrar kilidi tek REST isteğine indirger. */
  const gorunurOluncaUzlastir = () => {
    if (typeof document !== "undefined" && document.hidden) return;
    bekleyenGelenAramayiUzlastir().catch(() => {});
  };
  if (typeof document !== "undefined") {
    document.addEventListener("visibilitychange", gorunurOluncaUzlastir);
  }
  if (typeof window !== "undefined") {
    window.addEventListener("pageshow", gorunurOluncaUzlastir);
    window.addEventListener("focus", gorunurOluncaUzlastir);
  }
}

function guncelleKatilimci(liste, uid, yeniDurum) {
  const p = Array.isArray(liste) ? liste : [];
  const varMi = p.some((x) => String(x.uid) === String(uid));
  return varMi
    ? p.map((x) => (String(x.uid) === String(uid) ? { ...x, state: yeniDurum } : x))
    : [...p, { uid: String(uid), state: yeniDurum }];
}

export default {
  kur,
  aboneOl,
  durumuAl,
  kimlikAta,
  baslat,
  katil,
  cevapla,
  reddet,
  ayril,
  susturmaDegistir,
  hoparlorDegistir,
  odayiKontrolEt,
  ekraniAc,
  ekraniKucult,
};
